import { instance } from 'api/api';
import { APIResponse } from 'tools/types';
import { FormComponentValue } from 'components/common/form/layout/control';
import { ISaveReportContent } from 'form-templates/ReportForm';
import {
    DatasourceColumnsDataResponse,
    ReportFilterSegmentValuesData,
} from 'components/report-content/hooks/useReportContentSettings';
import {
    ComponentSettingsKpiChart,
    ComponentSettings,
    ComponentsType,
    DisplayMask,
    EditorData,
    IComponentProperties,
    IKpiChartOverlayDataRow,
    IReportState,
    ManageFiltersInstance,
    ManageFiltersRelevantValuesResponse,
} from '@report-content';

interface AiInsightResourceChip {
    key: string;
    label: string;
    resource_type: string;
    resource_id: number;
    type_label: string;
    logo_key: string;
    icon: { type: string; value: string };
    is_primary?: boolean;
    in_kg?: boolean;
}

export interface ReportPreviewComponent {
    internalName: ComponentsType | null;
    settings: ComponentSettingsKpiChart | null;
    displayMasks: Array<DisplayMask>;
}

interface AiInsightResourceCandidatesResponse {
    primary: AiInsightResourceChip | null;
    values: Array<AiInsightResourceChip>;
    // Present only while the report's own dataset is not admitted to the knowledge graph (MI-30225).
    primary_candidates?: Array<AiInsightResourceChip>;
    chosen_primary?: string | null;
}

// MI-30225: the effective primary picker's KG-gated pool, split by resource_type on the FE
// (Dataset / MCP Domain segment) — mirrors the External Report §6.2.3 control.
export interface AiInsightResourceCandidates {
    chips: Array<FormComponentValue>;
    primaryInKg: boolean;
    primaryDatasetId: number | null;
    primaryLabel: string | null;
    primaryCandidates: Array<FormComponentValue>;
    chosenPrimary: string | null;
}

const toFormComponentValue = (chip: AiInsightResourceChip): FormComponentValue => ({
    value: chip.key,
    label: chip.label,
    props: {
        is_primary: !!chip.is_primary,
        icon: chip.icon,
        resource_type: chip.resource_type,
        type_label: chip.type_label,
        logo_key: chip.logo_key,
    },
});

export const reportAPI = {
    async getAvailableKpiChartOverlays(elementId: number): Promise<Array<FormComponentValue>> {
        const response = await instance.get<{ values: Array<FormComponentValue> }>(
            `data/report/${elementId}/kpi-chart/overlay-metrics/available`,
        );
        return response.data.values;
    },

    async setKpiChartSummaryState(elementId: number, blockUid: string, isOpen: boolean): Promise<void> {
        await instance.post(`data/report/${elementId}/kpi-chart/${blockUid}/summary-state`, {
            state: isOpen ? 'Y' : 'N',
        });
    },

    async getKpiChartSummaryState(elementId: number, blockUid: string): Promise<boolean> {
        const response = await instance.get<{ state: boolean }>(
            `data/report/${elementId}/kpi-chart/${blockUid}/summary-state`,
        );
        return response.data.state;
    },

    async getKpiChartOverlaysData<T extends IComponentProperties>(
        elementId: number,
        overlayIds: Array<string>,
        contentSettings: T,
        filters: string | null,
    ): Promise<{ [overlayId: string]: Array<IKpiChartOverlayDataRow> }> {
        const settingsToPost = { ...contentSettings, instances: undefined };
        const response = await instance.post<{ overlays: { [overlayId: string]: Array<IKpiChartOverlayDataRow> } }>(
            `data/report/${elementId}/kpi-chart/overlays-data`,
            {
                overlayIds: overlayIds,
                properties: JSON.stringify(settingsToPost),
                filters: filters,
            },
        );
        return response.data.overlays;
    },

    async getReportState(elementId: number, segmentValueId: number, stateId?: number): Promise<IReportState> {
        const response = await instance.get<APIResponse<IReportState>>(
            `data/report/element/${elementId}/${segmentValueId}/state${stateId ? `/${stateId}` : ''}`,
        );
        return response.data.data;
    },

    async saveReportState(elementId: number, segmentValueId: number, state: IReportState['state']): Promise<number> {
        const response = await instance.post<APIResponse<{ id: number }>>(
            `data/report/element/${elementId}/${segmentValueId}/state`,
            { state },
        );
        return response.data.data.id;
    },

    async saveReportFilters(
        elementId: number,
        segmentValueId: number,
        filters: IReportState['filters'],
    ): Promise<number> {
        const response = await instance.post<APIResponse<{ id: number }>>(
            `data/report/element/${elementId}/${segmentValueId}/filters`,
            { filters },
        );
        return response.data.data.id;
    },

    async getFiltersRelevantValues(
        elementId: number,
        segmentValueId: number,
        filters: string | null,
    ): Promise<ManageFiltersRelevantValuesResponse> {
        const response = await instance.post<APIResponse<ManageFiltersRelevantValuesResponse>>(
            `data/report/element/${elementId}/${segmentValueId}/filters/relevant-values`,
            { filters },
        );
        return response.data.data;
    },

    async getContentEditorData(elementId: number): Promise<EditorData> {
        const response = await instance.get<APIResponse<EditorData>>(
            `data/editor/report/${elementId}/get-content-editor-data`,
        );
        return response.data.data;
    },

    async getComponentData<T extends IComponentProperties>(
        settings: ComponentSettings,
        contentSettings: T,
        filters: string | null,
    ): Promise<any> {
        const settingsToPost = { ...contentSettings, instances: undefined };

        const response = await instance.post<APIResponse<any>>(
            `data/report/component-data/${contentSettings.elementId}/${contentSettings.segmentValueId}`,
            {
                properties: JSON.stringify(settingsToPost),
                settings: JSON.stringify(settings),
                filters: filters,
            },
        );
        return response.data.data;
    },

    async getPreviewComponent(elementId: number): Promise<ReportPreviewComponent> {
        const response = await instance.get<APIResponse<ReportPreviewComponent>>(
            `data/report/${elementId}/preview-component`,
        );
        return response.data.data;
    },

    async getInstances(
        elementId: number,
        segmentValueId: number,
    ): Promise<{ instances: Array<ManageFiltersInstance>; hasInstances: boolean }> {
        if (!elementId) {
            return {
                instances: [],
                hasInstances: false,
            };
        }

        const response = await instance.get<APIResponse<any>>(`data/report/renderer-instances/${elementId}`, {
            params: {
                segment: segmentValueId,
            },
        });
        return {
            instances: response.data.data?.instances ?? [],
            hasInstances: response.data.data?.hasInstances ?? false,
        };
    },

    async getSegmentValuesList(
        elementId: number,
        segmentValueId: number,
        measurement_time?: string,
    ): Promise<ReportFilterSegmentValuesData> {
        let config = {};
        if (measurement_time) {
            config = {
                params: {
                    measurement_time: measurement_time,
                },
            };
        }
        const response = await instance.get<APIResponse<ReportFilterSegmentValuesData>>(
            `data/report/renderer-segments/${elementId}`,
            config,
        );
        return response.data.data;
    },
    async getColumnsSource(dataset: string, filter: string): Promise<boolean> {
        const response = await instance.get<APIResponse<DatasourceColumnsDataResponse>>(
            `data/dataset/${dataset}/get-columns-for-source/filter/${filter}`,
        );
        return response.data.data.has_access_map;
    },
    async getReportVisualizationList(elementId: number): Promise<Array<FormComponentValue>> {
        const response = await instance.get<APIResponse<Array<FormComponentValue>>>(
            `data/editor/report/${elementId}/get-external-visualizations-list`,
        );
        return response.data.data;
    },
    async getAiInsightResourceCandidates(elementId: number): Promise<AiInsightResourceCandidates> {
        const response = await instance.get<APIResponse<AiInsightResourceCandidatesResponse>>(
            `data/editor/report/${elementId}/ai-insight/resource-candidates`,
        );
        const { primary, values, primary_candidates = [], chosen_primary = null } = response.data.data;
        const primaryInKg = !!primary?.in_kg;
        // A not-admitted primary is not a real resource chip — the picker below replaces it.
        const chips = primary && primaryInKg ? [primary, ...values] : values;

        return {
            chips: chips.map(toFormComponentValue),
            primaryInKg,
            primaryDatasetId: primary?.resource_id ?? null,
            primaryLabel: primary?.label ?? null,
            primaryCandidates: primary_candidates.map(toFormComponentValue),
            chosenPrimary: chosen_primary,
        };
    },
    async saveReportContent(elementId: number, data: ISaveReportContent): Promise<any> {
        const response = await instance.post<APIResponse<Array<any>>>(
            `data/editor/report/save-renderer-blocks/element/${elementId}`,
            data,
        );
        return response.data;
    },
    async getStoplightMethods(): Promise<Array<FormComponentValue>> {
        const response = await instance.get<APIResponse<Array<FormComponentValue>>>(
            `data/editor/report/get-stoplight-calc-methods`,
        );
        return response.data.data;
    },
};
